import { Carousel, Lightbox, MediaContainer } from '@design-system';
import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import { InsightItem } from '@utils/journals/insights';
import { storyblokMediaItemToLightboxEntry } from '@utils/lightbox';
import { useTranslation } from 'next-i18next';
import React, { useMemo } from 'react';

import { VideoStoryblok } from '../../../types/generated-storyblok';
import { Image } from '../../Image';
import { JournalsCard } from '../../Journals';
import PageSection from '../../Layout/PageSection';

interface Props {
  videos: VideoStoryblok[];
  insights: InsightItem[];
}

export const ArtistInsightsTab: React.FC<Props> = ({ insights, videos }) => {
  const { t } = useTranslation(['artists']);

  const [emitter] = useAnalyticsEmitter();

  const handleSlide = ({
    length,
    position,
    title,
  }: {
    length: number;
    position: number;
    title: string;
  }) => {
    emitter('', 'carousel slide', {
      'carousel title': title,
      'carousel length': length,
      'slide position': position ? position + 1 : 0,
    });
  };

  const lightboxItems = useMemo(
    () => videos.map(storyblokMediaItemToLightboxEntry),
    [videos],
  );

  return (
    <div className="flex flex-col gap-9 md:gap-12">
      <PageSection className="flex flex-col gap-9 md:gap-12" fullWidth>
        {videos.length > 0 ? (
          <Lightbox items={lightboxItems}>
            {(lightbox) => (
              <Carousel
                headline={t('tabs.insights.sections.videos.title')}
                items={videos.map((item, i) => {
                  return (
                    <MediaContainer
                      duration={item.vimeo.vimeo_oembed.response.duration}
                      mediaComponent={
                        <Image
                          alt={item.coverImage.alt || ''}
                          className="overflow-hidden rounded-sm"
                          focus={item.coverImage.focus}
                          hoverEffect
                          size={{
                            sm: { ratio: '4/3', span: 12 },
                            md: { ratio: '4/3', span: 6 },
                            lg: { ratio: '4/3', span: 6 },
                          }}
                          src={item.coverImage.filename}
                        />
                      }
                      onClick={() => {
                        lightbox.openOnSlide(i);
                        emitter('', 'video click', {
                          position: i + 1,
                          'video title': item.title,
                          'video duration':
                            item.vimeo?.vimeo_oembed?.response?.duration,
                        });
                      }}
                      title={item.title}
                      type="Video"
                    />
                  );
                })}
                onSlide={(index) => {
                  handleSlide({
                    title: 'videos',
                    position: index,
                    length: videos.length,
                  });
                }}
                perPage={2}
              />
            )}
          </Lightbox>
        ) : null}

        {insights.length > 0 ? (
          <Carousel
            headline={t('tabs.insights.sections.articles.title')}
            items={insights.map((item, i) => {
              return (
                <JournalsCard
                  key={item.id}
                  description={item.intro}
                  eagerLoading={i === 0}
                  href={`/${item.full_slug}`}
                  image={item.image}
                  imageRatio={item.ratio || '4/3'}
                  mediaDuration={item.mediaDuration || ''}
                  onClick={() => {
                    emitter('', 'article click', {
                      position: i + 1,
                      'article slug': item.full_slug,
                      'article tag': item.tag,
                      'article title': item.title,
                    });
                  }}
                  publishedAt={item.date}
                  readingTime={item.readingTime}
                  tag={item.tag}
                  title={item.title}
                />
              );
            })}
            onSlide={(index) => {
              handleSlide({
                title: 'articles',
                position: index,
                length: insights.length,
              });
            }}
            perPage={3}
          />
        ) : null}
      </PageSection>
    </div>
  );
};
